import { defineDefaultEnumValue, defineEnumClampFunction } from 'src/lib/enum';

/**
 * Subscription plan category
 * @enum {string}
 */
export const SUBSCRIPTION_PLAN_CATEGORY = {
  basic: 'basic',
  starter: 'starter',
  dataDriven: 'dd_teams',
  business: 'business_self_serve',
  enterprise: 'enterprise',
  poc: 'poc',
};

/**
 * Subscription plan (active)
 * @enum {string}
 * @property {SUBSCRIPTION_PLAN_ACTIVE} DEFAULT - default active subscription plan
 * @property {EnumClampFunction<SUBSCRIPTION_PLAN_ACTIVE>} clamp - function that clamps the value to the enum
 */
export const SUBSCRIPTION_PLAN_ACTIVE = {
  basic: 'basic',
  starter: 'pp_starter',
  dataDriven: 'pp_data_driven',
  business: 'pp_business',
  businessPlus: 'pp_business_plus',
  businessMax: 'pp_business_max',
  performance: 'pp_performance',
  performancePlus: 'pp_performance_plus',
  performanceMax: 'pp_performance_max',
};
defineEnumClampFunction(SUBSCRIPTION_PLAN_ACTIVE, SUBSCRIPTION_PLAN_ACTIVE.starter);
defineDefaultEnumValue(SUBSCRIPTION_PLAN_ACTIVE, SUBSCRIPTION_PLAN_ACTIVE.starter);

export const SUBSCRIPTION_PLAN_LEGACY = {
  // Deprecated with "Autumn 2024 Pricing Changes", still have customers on it
  starterLegacy: 'starter',
  ddTeamsLegacy: 'dd_teams',
  businessSelfServeLegacy: 'business_self_serve',
  businessLegacy: 'business',
  pocLegacy: 'poc',

  // Deprecated some time ago, but we still have customers on it (might be "inactive" already),
  // so we still might use these values across FE to support existing customers
  basicTieredLegacy: 'basic_tiered',
  proPublisherLegacy: 'pro_beta_publisher',
  enterpriseLegacy: 'enterprise',

  // Deprecated some time ago, and we don't even have customers with it in DB,
  // so we should not have these values used across FE
  // Leaving them here just for future reference
  /*
    tieredLegacy: 'tiered',
    premiumLegacy: 'premium',
    continuousLegacy: 'continuous',
    dataDrivenLegacy: 'data_driven_beta_30',
    dataDriven100Legacy: 'data_driven_beta_100',
    acquisitionLegacy: 'acquisition',
    conversionLegacy: 'conversion',
    engagementLegacy: 'engagement',
    shopifyFreemiumLegacy: 'shopify_freemium',
    proAgencyLegacy: 'pro_beta_agency',
    proEcommerceLegacy: 'pro_beta_ecommerce',
    professionalLegacy: 'professional',
   */
};

/**
 * Subscription plan
 * @enum {string}
 * @property {SUBSCRIPTION_PLAN} DEFAULT - default subscription plan
 * @property {EnumClampFunction<SUBSCRIPTION_PLAN>} clamp - function that clamps the value to the enum
 */
export const SUBSCRIPTION_PLAN = {
  ...SUBSCRIPTION_PLAN_ACTIVE,
  ...SUBSCRIPTION_PLAN_LEGACY,
};
defineEnumClampFunction(SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN.starter);
defineDefaultEnumValue(SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN.starter);

/**
 * Billing method
 * @enum {string}
 */
export const BILLING_METHOD_TYPE = {
  stripe: 'stripe',
  storyDistro: 'story_distro',
  appsumo: 'appsumo',
};

/**
 * Subscription status
 * @enum {string}
 */
export const SUBSCRIPTION_STATUS = {
  noSubscription: 'no_subscription',
  active: 'active',
  canceled: 'canceled',
  incomplete: 'incomplete',
  incompleteExpired: 'incomplete_expired',
  pastDue: 'past_due',
  unpaid: 'unpaid',
};

export const SUBSCRIPTION_TIERED_TIER = {
  tier_migrate: '0_dollars_for_6000_words',
  tier_0: '0_dollars_for_5000_words',
  tier_19: '19_dollars_for_15000_words',
  tier_39: '39_dollars_for_35000_words',
  tier_99: '99_dollars_for_100000_words',
  tier_149: '149_dollars_for_165000_words',
  tier_199: '199_dollars_for_240000_words',
};

export const SUBSCRIPTION_PRO_TIER = {
  pro_beta_agency_tier_1: 'pro_beta_agency_tier_1',
  pro_beta_agency_tier_2: 'pro_beta_agency_tier_2',
  pro_beta_ecommerce_tier_1: 'pro_beta_ecommerce_tier_1',
  pro_beta_ecommerce_tier_2: 'pro_beta_ecommerce_tier_2',
  pro_beta_publisher_tier_1: 'pro_beta_publisher_tier_1',
  pro_beta_publisher_tier_2: 'pro_beta_publisher_tier_2',
};

export const SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER = {
  seats_3: 'seats_3',
  seats_4: 'seats_4',
  seats_5: 'seats_5',
  seats_6: 'seats_6',
  seats_7: 'seats_7',
  seats_8: 'seats_8',
  seats_9: 'seats_9',
  seats_10: 'seats_10',
};

export const SUBSCRIPTION_SEAT_TIER = {
  ...SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  seats_11: 'seats_11',
  seats_12: 'seats_12',
  seats_13: 'seats_13',
  seats_14: 'seats_14',
  seats_15: 'seats_15',
  seats_16: 'seats_16',
  seats_17: 'seats_17',
  seats_18: 'seats_18',
  seats_19: 'seats_19',
  seats_20: 'seats_20',
};

/**
 * Subscription tier
 * @enum {string}
 * @property {EnumClampFunction<SUBSCRIPTION_TIER>} clamp - function that clamps the value to the enum
 */
export const SUBSCRIPTION_TIER = {
  ...SUBSCRIPTION_TIERED_TIER,
  ...SUBSCRIPTION_PRO_TIER,
  ...SUBSCRIPTION_SEAT_TIER,
};
defineEnumClampFunction(SUBSCRIPTION_TIER);

/**
 * Billing cycles
 * @enum {string}
 * @property {EnumClampFunction<BILLING_CYCLES>} clamp - function that clamps the value to the enum
 */
export const BILLING_CYCLES = {
  monthly: 'monthly',
  annually: 'annual',
};
defineEnumClampFunction(BILLING_CYCLES, BILLING_CYCLES.monthly);

export const ANNUAL_BILLING_CYCLE_DISCOUNT_PERCENT = 0.2;

export const TRIAL_WORDS_LIMIT = 2500;
